/**
 * FeatureGrid — the scrollable feature list: category groups of FeatureCards,
 * the whitelist-mode banner, and the empty/no-results state.
 *
 * Extracted from FeatureManagerWindow; behavior-preserving. Pure presentation
 * over already-grouped data — it owns no feature state, only renders + relays
 * per-feature callbacks up to the orchestrator.
 */
import React from 'react';
import { COLORS, SPACE, RADII, FONT_SIZE, FONT_WEIGHT } from '../../theme';
import { FeatureMode, featureFlags } from '../../../../core/features';
import type { FeatureConfig } from '../../../../core/features';
import { FeatureCard } from './FeatureCard';
import {
  featureListStyle,
  groupTitleStyle,
  emptyStateStyle,
} from './featureManagerStyles';

export interface GroupedFeature {
  name: string;
  config: FeatureConfig;
}

export interface FeatureGridProps {
  domain: string;
  groupedFeatures: Record<string, GroupedFeature[]>;
  currentMode: FeatureMode;
  whitelistState: string[];
  searchQuery: string;
  onToggle: (feature: string) => void;
  onWhitelistToggle: (feature: string) => void;
  onSolo: (feature: string) => void;
}

const groupStyle: React.CSSProperties = { marginBottom: SPACE.xxxl };

const whitelistBannerWrapStyle: React.CSSProperties = {
  padding: `0 ${SPACE.xxl}px`,
  marginBottom: SPACE.lg,
};

const whitelistBannerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: SPACE.sm,
  padding: `${SPACE.sm}px ${SPACE.lg}px`,
  borderRadius: RADII.md,
  background: `${COLORS.warning}1a`,
  color: COLORS.warning,
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.semibold,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

export const FeatureGrid: React.FC<FeatureGridProps> = ({
  domain,
  groupedFeatures,
  currentMode,
  whitelistState,
  searchQuery,
  onToggle,
  onWhitelistToggle,
  onSolo,
}) => {
  const isEmpty = Object.keys(groupedFeatures).length === 0;

  return (
    <>
      {/* Whitelist-mode banner */}
      {currentMode === FeatureMode.WHITELIST && (
        <div style={whitelistBannerWrapStyle}>
          <div style={whitelistBannerStyle}>
            <span>🧪</span>
            Whitelist Mode Active
            <span style={{ opacity: 0.6 }}>|</span>
            <span>{whitelistState.length} features shown</span>
          </div>
        </div>
      )}

      {/* Feature list */}
      <div style={featureListStyle}>
        {Object.entries(groupedFeatures).map(([category, features]) => (
          <div key={category} style={groupStyle}>
            <div style={groupTitleStyle}>{category}</div>
            {features.map(({ name, config }) => {
              const isEnabled = config.enabled;
              const isWhitelisted = whitelistState.includes(name);
              const isVisible = featureFlags.isVisible(domain, name);

              return (
                <FeatureCard
                  key={name}
                  name={name}
                  config={config}
                  isEnabled={isEnabled}
                  isWhitelisted={isWhitelisted}
                  isVisible={isVisible}
                  dimmed={currentMode === FeatureMode.NORMAL && !isEnabled}
                  onToggle={() => onToggle(name)}
                  onWhitelistToggle={() => onWhitelistToggle(name)}
                  onSolo={() => onSolo(name)}
                />
              );
            })}
          </div>
        ))}

        {isEmpty && (
          <div style={emptyStateStyle}>
            <div style={{ fontSize: 32, marginBottom: SPACE.lg }}>🔍</div>
            <div>No features found</div>
            {searchQuery && (
              <div style={{ fontSize: FONT_SIZE.md, marginTop: SPACE.md }}>
                Try a different search
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
